import { createFileRoute, useRouter } from "@tanstack/react-router";
import React from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "@tanstack/react-form";
import Form from "src/components/base/form";
import { ErrorMessage, Field, Fieldset, Label, Legend } from "src/components/base/fieldset";
import { Radio, RadioField, RadioGroup } from "src/components/base/radio";
import { Input } from "src/components/base/input";
import { Text } from "src/components/base/text";
import { Button, PrimaryButton } from "src/components/base/button";
import { Divider } from "src/components/base/divider";
import { ApiInternal } from "src/api/api_internal";
import { toast } from "react-toastify";
import { GlobalDnsType } from "src/api/generated/frontend_internal_v1";

/**
 * The properties for {@link GlobalDnsOverview}
 */
export type GlobalDnsOverviewProps = {};

/**
 * An overview of all Global DNS
 */
export default function GlobalDnsOverview(props: GlobalDnsOverviewProps) {
    const [t] = useTranslation("admin-dns");
    const [tg] = useTranslation();

    const router = useRouter();
    const data = Route.useLoaderData();

    const [customSelected, setCustomSelected] = React.useState(data.dns_type);

    const form = useForm({
        defaultValues: {
            dns: data.dns_type,
            ipv4provider1: data.ipv4.at(0) ?? "",
            ipv4provider2: data.ipv4.at(1) ?? "",
            ipv4provider3: data.ipv4.at(2) ?? "",
            ipv6provider1: data.ipv6.at(0) ?? "",
            ipv6provider2: data.ipv6.at(1) ?? "",
            ipv6provider3: data.ipv6.at(2) ?? "",
        },
        validators: {
            onSubmitAsync: async ({
                value: {
                    dns,
                    ipv4provider1,
                    ipv4provider2,
                    ipv4provider3,
                    ipv6provider1,
                    ipv6provider2,
                    ipv6provider3,
                },
            }) => {
                const ipv4 = [ipv4provider1, ipv4provider2, ipv4provider3].flatMap((val) => (val !== "" ? [val] : []));
                const ipv6 = [ipv6provider1, ipv6provider2, ipv6provider3].flatMap((val) => (val !== "" ? [val] : []));

                if (ipv4.length === 0 && ipv6.length === 0) {
                    return {
                        fields: {
                            ipv4provider1: t("error.one-ip-should-provided"),
                            ipv6provider1: t("error.one-ip-should-provided"),
                        },
                    };
                }

                const res = await ApiInternal.dns.update({
                    dns_type: dns,
                    ipv4: ipv4,
                    ipv6: ipv6,
                });

                if (res.result === "Err") {
                    return {
                        fields: {
                            ipv4provider1: res.error.ipv4_provider1
                                ? t("error.ipv4-invalid")
                                : res.error.missing_ip_address
                                  ? t("error.one-ip-should-provided")
                                  : undefined,
                            ipv4provider2: res.error.ipv4_provider2 ? t("error.ipv4-invalid") : undefined,
                            ipv4provider3: res.error.ipv4_provider3 ? t("error.ipv4-invalid") : undefined,
                            ipv6provider1: res.error.ipv6_provider1
                                ? t("error.ipv6-invalid")
                                : res.error.missing_ip_address
                                  ? t("error.one-ip-should-provided")
                                  : undefined,
                            ipv6provider2: res.error.ipv6_provider2 ? t("error.ipv6-invalid") : undefined,
                            ipv6provider3: res.error.ipv6_provider3 ? t("error.ipv6-invalid") : undefined,
                        },
                    };
                }
            },
        },
        onSubmit: () => {
            toast.success(t("toast.success"));
            router.invalidate({ sync: true });
        },
    });

    return (
        <div className={"flex flex-col gap-4"}>
            <Form className={"flex max-w-lg flex-col gap-12"} onSubmit={form.handleSubmit}>
                <Fieldset>
                    <Legend>{t("heading.global-dns-legend")}</Legend>
                    <Text>{t("description.global-dns")}</Text>
                    <form.Field name={"dns"}>
                        {(fieldApi) => (
                            <RadioGroup
                                name={"dns"}
                                defaultValue={GlobalDnsType.Google}
                                value={fieldApi.state.value}
                                onChange={(e) => {
                                    fieldApi.handleChange(e as GlobalDnsType);
                                    setCustomSelected(e as GlobalDnsType);
                                }}
                            >
                                <RadioField>
                                    <Radio value={GlobalDnsType.Google} />
                                    <Label>{t("label.google")}</Label>
                                </RadioField>
                                <RadioField>
                                    <Radio value={GlobalDnsType.Cloudflare} />
                                    <Label>{t("label.cloudflare")}</Label>
                                </RadioField>
                                <RadioField>
                                    <Radio value={GlobalDnsType.Quad9} />
                                    <Label>{t("label.quad9")}</Label>
                                </RadioField>
                                <RadioField>
                                    <Radio value={GlobalDnsType.Custom} />
                                    <Label>{t("label.custom")}</Label>
                                </RadioField>
                            </RadioGroup>
                        )}
                    </form.Field>
                </Fieldset>
                <Fieldset className={"flex flex-col gap-4"}>
                    <Legend>{t("heading.custom-dns-global-ipv4")}</Legend>
                    <Text>{t("description.custom-dns-global-ipv4")}</Text>
                    <form.Field
                        name={"ipv4provider1"}
                        validators={{
                            onChangeListenTo: [
                                "dns",
                                "ipv4provider2",
                                "ipv4provider3",
                                "ipv6provider1",
                                "ipv6provider2",
                                "ipv6provider3",
                            ],
                            onChange: ({ value, fieldApi }) => {
                                if (
                                    fieldApi.form.getFieldValue("dns") === GlobalDnsType.Custom &&
                                    value === "" &&
                                    fieldApi.form.getFieldValue("ipv4provider2") === "" &&
                                    fieldApi.form.getFieldValue("ipv4provider3") === "" &&
                                    fieldApi.form.getFieldValue("ipv6provider1") === "" &&
                                    fieldApi.form.getFieldValue("ipv6provider2") === "" &&
                                    fieldApi.form.getFieldValue("ipv6provider3") === ""
                                ) {
                                    return t("error.one-ip-should-provided");
                                }
                                return undefined;
                            },
                        }}
                    >
                        {(fieldApi) => (
                            <Field>
                                <Label>{t("label.provider-1")}</Label>
                                <Input
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    disabled={customSelected !== GlobalDnsType.Custom}
                                    invalid={fieldApi.state.meta.errors.length > 0}
                                />
                                {fieldApi.state.meta.errors.map((err) => (
                                    <ErrorMessage key={err}>{err}</ErrorMessage>
                                ))}
                            </Field>
                        )}
                    </form.Field>
                    <form.Field name={"ipv4provider2"}>
                        {(fieldApi) => (
                            <Field>
                                <Label>{t("label.provider-2")}</Label>
                                <Input
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    disabled={customSelected !== GlobalDnsType.Custom}
                                    invalid={fieldApi.state.meta.errors.length > 0}
                                />
                                {fieldApi.state.meta.errors.map((err) => (
                                    <ErrorMessage key={err}>{err}</ErrorMessage>
                                ))}
                            </Field>
                        )}
                    </form.Field>
                    <form.Field name={"ipv4provider3"}>
                        {(fieldApi) => (
                            <Field>
                                <Label>{t("label.provider-3")}</Label>
                                <Input
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    disabled={customSelected !== GlobalDnsType.Custom}
                                    invalid={fieldApi.state.meta.errors.length > 0}
                                />
                                {fieldApi.state.meta.errors.map((err) => (
                                    <ErrorMessage key={err}>{err}</ErrorMessage>
                                ))}
                            </Field>
                        )}
                    </form.Field>
                </Fieldset>
                <Fieldset className={"flex flex-col gap-4"}>
                    <Legend>{t("heading.custom-dns-global-ipv6")}</Legend>
                    <Text>{t("description.custom-dns-global-ipv6")}</Text>
                    <form.Field
                        name={"ipv6provider1"}
                        validators={{
                            onChangeListenTo: [
                                "dns",
                                "ipv4provider1",
                                "ipv4provider2",
                                "ipv4provider3",
                                "ipv6provider2",
                                "ipv6provider3",
                            ],
                            onChange: ({ value, fieldApi }) => {
                                if (
                                    fieldApi.form.getFieldValue("dns") === GlobalDnsType.Custom &&
                                    value === "" &&
                                    fieldApi.form.getFieldValue("ipv4provider1") === "" &&
                                    fieldApi.form.getFieldValue("ipv4provider2") === "" &&
                                    fieldApi.form.getFieldValue("ipv4provider3") === "" &&
                                    fieldApi.form.getFieldValue("ipv6provider2") === "" &&
                                    fieldApi.form.getFieldValue("ipv6provider3") === ""
                                ) {
                                    return t("error.one-ip-should-provided");
                                }
                                return undefined;
                            },
                        }}
                    >
                        {(fieldApi) => (
                            <Field>
                                <Label>{t("label.provider-1")}</Label>
                                <Input
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    disabled={customSelected !== GlobalDnsType.Custom}
                                    invalid={fieldApi.state.meta.errors.length > 0}
                                />
                                {fieldApi.state.meta.errors.map((err) => (
                                    <ErrorMessage key={err}>{err}</ErrorMessage>
                                ))}
                            </Field>
                        )}
                    </form.Field>
                    <form.Field name={"ipv6provider2"}>
                        {(fieldApi) => (
                            <Field>
                                <Label>{t("label.provider-2")}</Label>
                                <Input
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    disabled={customSelected !== GlobalDnsType.Custom}
                                    invalid={fieldApi.state.meta.errors.length > 0}
                                />
                                {fieldApi.state.meta.errors.map((err) => (
                                    <ErrorMessage key={err}>{err}</ErrorMessage>
                                ))}
                            </Field>
                        )}
                    </form.Field>
                    <form.Field name={"ipv6provider3"}>
                        {(fieldApi) => (
                            <Field>
                                <Label>{t("label.provider-3")}</Label>
                                <Input
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    disabled={customSelected !== GlobalDnsType.Custom}
                                    invalid={fieldApi.state.meta.errors.length > 0}
                                />
                                {fieldApi.state.meta.errors.map((err) => (
                                    <ErrorMessage key={err}>{err}</ErrorMessage>
                                ))}
                            </Field>
                        )}
                    </form.Field>
                </Fieldset>
                <Divider />
                <div className={"flex justify-end gap-3"}>
                    <Button plain={true} onClick={() => form.reset()}>
                        {t("button.reset")}
                    </Button>
                    <PrimaryButton type="submit"> {tg("button.save")}</PrimaryButton>
                </div>
            </Form>
        </div>
    );
}

export const Route = createFileRoute("/_admin/a/dns/_dns/global")({
    component: GlobalDnsOverview,
    loader: async () => await ApiInternal.dns.get(),
});
